<template>
  <v-card flat dense>
    <v-form ref="form" v-model="valid">
      <v-card-title>
        <span class="mr-4">
          {{ $t('ITEM.CREATE') }} {{ $t('PLANNINGS.TASKS.EVENT.ITEM') }}
        </span>
        <v-spacer></v-spacer>
        <span class="text-body-2">{{ $t('ITEM.STATUS') }}</span>
        <div>
          <v-select
            v-model="task.status_id"
            name="status_id"
            item-value="id"
            item-text="value"
            :items="[]"
            :error-messages="errors.status_id"
            disabled
          ></v-select>
        </div>
        <v-btn icon @click="cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle v-if="errorsWithoutForm.length">
        <errors :errors="errorsWithoutForm" />
      </v-card-subtitle>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <order-sheet
              v-if="orderId"
              class="pa-4"
              :order="order"
              :show-fields="[
                'order_number',
                'customer',
                'address',
                'mobile_phone',
                'phone',
                'email',
                'service',
                'dateServiceExpected',
                'duration',
                'description'
              ]"
              show-title
              rounded
            ></order-sheet>
          </v-col>
          <v-col cols="12" md="8">
            <v-sheet class="pa-3" outlined rounded>
              <v-row no-gutters>
                <v-col cols="12">
                  <p class="text-h6 text--primary">
                    {{ $t('PLANNINGS.TASK_DETAILS') }}
                  </p>
                </v-col>
                <v-col cols="12">
                  <event-form
                    v-model="task"
                    :order="order"
                    :errors="errors.getAll()"
                    :disable-multi-task="selectedDay.past"
                    @update-prop="clearErrorByName($event)"
                  ></event-form>
                </v-col>
              </v-row>
            </v-sheet>
          </v-col>
        </v-row>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn outlined depressed color="grey" @click="cancel">
            {{ $t('BUTTON.CANCEL') }}
          </v-btn>
          <v-btn
            depressed
            color="primary"
            :disabled="!valid"
            :loading="loading"
            @click="confirmCreate"
          >
            {{ $t('BUTTON.SAVE') }}
          </v-btn>
          <confirm-dialog ref="confirm"></confirm-dialog>
        </v-card-actions>
      </v-card-text>
    </v-form>
  </v-card>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters, mapState } from 'vuex';
import { dateFormat } from '@/modules/_global/filters';
import errors from '@/modules/_global/mixins/errors';
import Errors from '@/modules/_global/_components/Errors';
import OrderSheet from '../OrderSheet';
import EventForm from '../forms/Event/EventForm';
import { onceRequest } from '@/modules/_global/utils/common';
import { cloneDeep } from 'lodash';

export default {
  name: 'CreateEventTask',

  filters: {
    dateFormat
  },

  components: {
    EventForm,
    OrderSheet,
    Errors,
    ConfirmDialog: () => import('@/modules/_global/_components/ConfirmDialog')
  },

  mixins: [errors],

  props: {
    taskToCopy: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      valid: false,
      task: {},
      order: {},
      confirmationHolidayDialog: false
    };
  },

  computed: {
    ...mapState(['loading']),
    ...mapState('planning', ['selectedDay', 'selectedEntity']),
    ...mapGetters('planning', ['SELECTED_ORDER']),
    orderId() {
      return this.task.order_id ? this.task.order_id : this.SELECTED_ORDER.id;
    },
    employeeTitle() {
      const first = this.selectedEntity.first_name || '';
      const last = this.selectedEntity.last_name || '';

      return `${first} ${last}`.trim();
    },

    errorsWithoutForm() {
      return ['order_id', 'employee_id', 'date_from', '_unexpected']
        .reduce((acc, i) => acc.concat(this.errors.get(i)), [])
        .filter(i => i);
    }
  },

  watch: {
    taskToCopy: {
      deep: true,
      immediate: true,
      handler(e) {
        if (!e) {
          return;
        }

        this.task = Object.assign({}, this.task, e);

        if (!this.task.order_id) {
          return;
        }
        this.getOrdersList(this.task.order_id);
      }
    }
  },

  mounted() {
    if (!this.task.order_id) {
      this.getOrdersList();
    }

    this.checkHoliday({
      id: this.orderId,
      date: this.selectedDay.date
    }).then(({ data }) => {
      this.confirmationHolidayDialog = data.data;
    });

    this.initForm();
  },

  methods: {
    ...mapActions('planning', ['storeTask', 'addSnack', 'checkHoliday']),
    ...mapActions('order', ['getOrder']),

    getOrdersList(orderId = this.SELECTED_ORDER.id) {
      if (!orderId) return '';
      this.getOrder({ orderId: orderId }).then(({ data }) => {
        this.order = data;
      });
    },

    initForm() {
      const date = this.selectedDay.date;

      this.task = {
        employee: this.selectedEntity,
        order_id: this.orderId,
        employee_id: this.selectedEntity.id,
        date_from: date,
        start_date: date,
        end_date: moment(date)
          .add(3, 'month')
          .format(moment.HTML5_FMT.DATE)
      };
    },
    cancel() {
      this.$emit('cancel');
    },

    confirmCreate: onceRequest(function confirmCreate() {
      if (this.confirmationHolidayDialog) {
        this.$refs.confirm
          .open(null, `${this.$t('HOLIDAY.TASK_CONFIRM')}`, {
            messageClass: 'text-center text-md-h6 text-lg-h5 text-xl-h4',
            cancelBtnText: this.$t('BUTTON.CANCEL'),
            agreeBtnText: this.$t('BUTTON.CREATE'),
            agreeBtnColor: 'success'
          })
          .then(confirm => {
            if (confirm) {
              return this.create();
            }
          });

        return;
      }

      return this.create();
    }),

    create() {
      const task = {
        ...this.task,
        ...this.task.multi_task
      };

      return this.storeTask({ task })
        .then(response => {
          const { errors, data } = response.data;

          this.$emit('created', data);

          if (errors && errors.length) {
            errors.forEach(item => {
              this.addSnack({
                color: 'orange accent-2',
                type: 'warning',
                timeout: 3600000,
                message: item
              });
            });
          }
        })
        .catch(({ response }) => {
          this.handleErrorsByResponse(response);
        });
    }
  }
};
</script>
